import { createWebStoragePersister } from 'react-query/createWebStoragePersister'
import {
  chain,
  configureChains,
  Connector,
  createClient,
  createStorage,
  deserialize,
  serialize
} from 'wagmi'
import { CoinbaseWalletConnector } from 'wagmi/connectors/coinbaseWallet'
import { InjectedConnector } from 'wagmi/connectors/injected'
import { MetaMaskConnector } from 'wagmi/connectors/metaMask'
import { WalletConnectConnector } from 'wagmi/connectors/walletConnect'
import { alchemyProvider } from 'wagmi/providers/alchemy'
import { publicProvider } from 'wagmi/providers/public'
import MagicConnector from './MagicConnector'

const magicApiKey = window.MAGIC_PUBLISHABLE_KEY
const alchemyId = window.ALCHEMY_API_KEY

const { chains, provider, webSocketProvider } = configureChains(
  [chain.mainnet, chain.goerli, chain.polygon, chain.polygonMumbai],
  [
    publicProvider({ priority: 1, stallTimeout: 1000 }),
    alchemyProvider({ priority: 0, stallTimeout: 1000, alchemyId })
  ]
)

// Must equal the keys in the Connectors dictionary
export type WalletProviderType =
  | 'metaMask'
  | 'coinbaseWallet'
  | 'walletConnect'
  | 'injected'
  | 'magicLink'
export const Connectors: { [key: string]: Connector } = {
  metaMask: new MetaMaskConnector({
    chains,
    options: {
      shimDisconnect: false
    }
  }),
  coinbaseWallet: new CoinbaseWalletConnector({
    chains,
    options: {
      appName: 'Arpeggi Labs'
    }
  }),
  walletConnect: new WalletConnectConnector({
    chains,
    options: {
      qrcode: true,
      qrcodeModalOptions: {}
    }
  }),
  injected: new InjectedConnector({
    chains,
    options: {
      name: 'Injected',
      shimDisconnect: true
    }
  }),
  magicLink: new MagicConnector({
    chains,
    options: {
      apiKey: magicApiKey,
      enableEmailLogin: true,
      enableSMSlogin: false
    }
  })
}
export const WalletProvider: { [key: string]: WalletProviderType } = {
  METAMASK: 'metaMask',
  COINBASE: 'coinbaseWallet',
  WALLETCONNECT: 'walletConnect',
  INJECTED: 'injected',
  MAGICLINK: 'magicLink'
}

const connectorsList: Connector[] = Object.values(Connectors)

const isLocalStorageAvailable = () => {
  try {
    localStorage.getItem('whatever')
  } catch (err) {
    console.log('local storage not available. possibly running in an iframe')
    return false
  }
  return true
}

class InMemoryStorage<K, V> {
  private map: Map<K, V> = new Map()

  get length(): number {
    return this.map.size
  }

  clear() {
    this.map.clear()
  }

  getItem(key: K): V | null {
    return this.map.get(key) || null
  }

  key(index: number): V | null {
    return Array.from(this.map.values())[index] || null
  }

  removeItem(key: K): void {
    this.map.delete(key)
  }

  setItem(key: K, value: V) {
    this.map.set(key, value)
  }

  [name: string]: any
}

const Web3Client = createClient({
  connectors: connectorsList,
  provider,
  persister:
    !isLocalStorageAvailable() &&
    createWebStoragePersister({
      key: 'wagmi.cache',
      storage: new InMemoryStorage<string, any>(),
      serialize,
      deserialize
    }),
  storage: isLocalStorageAvailable()
    ? createStorage({ storage: window.localStorage })
    : new InMemoryStorage<string, any>()
})

export default Web3Client
